import React, { useId } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, FormControlLabel, MenuItem, TextField, Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { C } from "../reporting/reportingUtils";

const SECTIONS = {
  application: {
    title: "Review extracted fields",
    description: "Compare each value with its source and correct it before marking it reviewed.",
    empty: "No application fields were extracted.",
    note: "A reviewed field records your review of the application. It does not verify an entity or approve credit or insurance.",
  },
  salesReceivables: {
    title: "Sales & Receivables",
    description: "Review the sales and debtor information declared in this application.",
    empty: "No sales or receivables fields were extracted.",
    note: "Application declarations are shown as supplied, with their original units and periods. These values do not update the live receivables portfolio.",
  },
  losses: {
    title: "Losses",
    description: "Review the loss history, recoveries and reporting periods declared in this application.",
    empty: "No loss history fields were extracted.",
    note: "Declared losses and recoveries are application evidence. Reviewing them does not file a claim or confirm insurer acceptance.",
  },
};

const displayValue = (value) => value === null || value === undefined ? "" : String(value);
const hasValue = (value) => displayValue(value).trim() !== "";
const validPage = (page) => Number.isInteger(page) && page > 0;
const sourceVerified = (field) => field.provenanceStatus === "verified"
  && validPage(field.sourcePage) && Boolean(field.sourceQuote?.trim());
const validConfidence = (value) => typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1;
const fieldWarnings = (field) => Array.isArray(field.warnings) ? field.warnings.filter((warning) => typeof warning === "string" && warning.trim()) : [];

function FieldReview({ field, index, onUpdate, onSourcePage, disabled }) {
  const rowId = useId();
  const label = field.label || `Application field ${index + 1}`;
  const verified = sourceVerified(field);
  const confidence = validConfidence(field.confidence) ? Math.round(field.confidence * 100) : null;
  const original = Object.prototype.hasOwnProperty.call(field, "originalValue") ? field.originalValue : field.value;
  const edited = displayValue(field.value) !== displayValue(original);
  const warnings = fieldWarnings(field);
  const booleanField = typeof original === "boolean";
  const updateValue = (input) => {
    let value = input;
    if (booleanField) value = input === "" ? null : input === "true";
    else if (typeof original === "number") {
      // Preserve a number only when the reviewer entered an unambiguous numeric value.
      // Empty values stay missing; currency/period annotations are retained verbatim.
      const text = input.trim();
      value = text === "" ? null : /^[-+]?(?:\d+\.?\d*|\.\d+)$/.test(text) && Number.isFinite(Number(text)) ? Number(text) : input;
    }
    onUpdate({ ...field, value, originalValue: original, reviewStatus: "needs_review" });
  };

  return <Box component="article" aria-labelledby={`${rowId}-label`} sx={{
    borderTop: `1px solid ${C.line}`, p: { xs: 2, md: 2.5 },
    background: verified ? "#fff" : "#FFFCF6",
  }}>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(140px, .9fr) minmax(180px, 1.25fr) minmax(140px, .8fr)" }, gap: 2, alignItems: "start" }}>
      <Box>
        <Typography id={`${rowId}-label`} component="h3" sx={{ color: C.ink, fontSize: 13, fontWeight: 700 }}>{label}</Typography>
        {field.unit && <Typography sx={{ color: C.muted, fontSize: 12, mt: .5 }}>Unit: {field.unit}</Typography>}
        <Box sx={{ display: "flex", alignItems: "center", gap: .6, flexWrap: "wrap", mt: 1 }}>
          {validPage(field.sourcePage) ? <Button size="small" variant="text" disabled={!onSourcePage}
            startIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />}
            aria-label={`Open ${verified ? "source" : "claimed source"} page ${field.sourcePage} for ${label}`}
            onClick={() => onSourcePage(field.sourcePage, field)}
            sx={{ fontSize: 12, p: 0, minWidth: 0, textTransform: "none", justifyContent: "flex-start" }}>
            {verified ? "Source page" : "Claimed page"} {field.sourcePage}
          </Button> : <Typography sx={{ fontSize: 12, color: "#865A12" }}>Source page unavailable</Typography>}
        </Box>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <TextField fullWidth size="small" select={booleanField} disabled={disabled}
          label={label} value={displayValue(field.value)}
          onChange={(event) => updateValue(event.target.value)}
          inputProps={{ "aria-describedby": `${rowId}-evidence ${rowId}-original` }}
          placeholder="Not provided in the application"
          sx={{ "& .MuiOutlinedInput-root": { background: "#fff", borderRadius: "7px" } }}>
          {booleanField && [<MenuItem key="missing" value="">Not provided</MenuItem>, <MenuItem key="true" value="true">Yes</MenuItem>, <MenuItem key="false" value="false">No</MenuItem>]}
        </TextField>
        <Box id={`${rowId}-original`} sx={{ mt: .8, display: "flex", alignItems: "flex-start", gap: .7 }}>
          {edited && <Chip size="small" label="Edited" sx={{ height: 20, background: "#EDF3FF", color: C.blue, fontSize: 10.5 }} />}
          <Typography sx={{ color: C.muted, fontSize: 11.5, overflowWrap: "anywhere" }}>
            {edited ? `Originally extracted: ${hasValue(original) ? displayValue(original) : "Not provided"}` : !hasValue(field.value) ? "Missing value — this is not a zero declaration." : "Original extracted value retained."}
          </Typography>
        </Box>
      </Box>

      <Box>
        <Typography sx={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: ".035em", fontWeight: 700 }}>Extraction estimate</Typography>
        <Typography sx={{ color: confidence === null ? C.muted : confidence < 80 ? "#865A12" : C.slate, fontSize: 13, fontWeight: 700, mt: .4 }}>
          {confidence === null ? "Confidence not supplied" : `${confidence}% confidence`}
        </Typography>
        <FormControlLabel sx={{ ml: -1, mr: 0, mt: .4, alignItems: "center", "& .MuiFormControlLabel-label": { fontSize: 12, color: C.slate } }}
          control={<Checkbox size="small" disabled={disabled} checked={field.reviewStatus === "reviewed"}
            inputProps={{ "aria-label": `Mark ${label} reviewed` }}
            onChange={(event) => onUpdate({ ...field, reviewStatus: event.target.checked ? "reviewed" : "needs_review" })} />}
          label={field.reviewStatus === "reviewed" ? "Reviewed" : "Mark reviewed"} />
      </Box>
    </Box>

    <Box id={`${rowId}-evidence`} sx={{ mt: 1.5, p: 1.25, borderRadius: "7px", background: verified ? C.ground : "#FFF5E1", border: `1px solid ${verified ? C.line : "#F1DCB3"}` }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: verified ? C.slate : "#865A12" }}>
        {verified ? "Source excerpt located on this page" : "Source evidence needs verification"}
      </Typography>
      {field.sourceQuote ? <Typography component="blockquote" sx={{ m: "6px 0 0", fontSize: 12, color: C.slate, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{field.sourceQuote}</Typography>
        : <Typography sx={{ mt: .4, fontSize: 12, color: "#865A12" }}>No supporting excerpt was supplied. Check the original application before relying on this value.</Typography>}
      {!verified && field.sourceQuote && <Typography sx={{ mt: .5, fontSize: 11.5, color: "#865A12" }}>The excerpt has not been verified against the cited page. Human review does not change this evidence status.</Typography>}
      {warnings.length > 0 && <Box component="ul" sx={{ pl: 2, mb: 0, mt: .8, color: "#865A12", fontSize: 12 }}>{warnings.map((warning, warningIndex) => <li key={`${warningIndex}-${warning}`}>{warning}</li>)}</Box>}
    </Box>
  </Box>;
}

export default function InsuranceApplicationReview({ extraction, category = "application", onChange, onSourcePage, disabled = false }) {
  const headingId = useId();
  const section = SECTIONS[category] || SECTIONS.application;
  const allFields = Array.isArray(extraction?.fields) ? extraction.fields : [];
  const fields = allFields.map((field, index) => ({ field, index })).filter(({ field }) => field && field.category === category);
  const reviewed = fields.filter(({ field }) => field.reviewStatus === "reviewed").length;
  const unverified = fields.filter(({ field }) => !sourceVerified(field)).length;
  const updateField = (index, updated) => {
    if (disabled || typeof onChange !== "function") return;
    onChange({ ...extraction, fields: allFields.map((field, fieldIndex) => fieldIndex === index ? updated : field) });
  };

  return <Box component="section" aria-labelledby={headingId} sx={{ fontFamily: "inherit", color: C.ink }}>
    <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5, flexWrap: "wrap" }}>
      <Box><Typography id={headingId} component="h2" sx={{ fontSize: 18, fontWeight: 800 }}>{section.title}</Typography>
        <Typography sx={{ color: C.muted, fontSize: 13, mt: .5 }}>{section.description}</Typography></Box>
      {fields.length > 0 && <Chip size="small" label={`${reviewed} of ${fields.length} reviewed`} sx={{ background: "#EDF3FF", color: C.blue, fontWeight: 700 }} />}
    </Box>

    {fields.length === 0 ? <Box sx={{ p: { xs: 3, md: 4 }, background: "#fff", border: `1px dashed ${C.line}`, borderRadius: "10px", textAlign: "center" }}>
      <DescriptionOutlinedIcon aria-hidden="true" sx={{ color: C.muted, mb: 1, fontSize: 30 }} />
      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{section.empty}</Typography>
      <Typography sx={{ color: C.muted, fontSize: 13, mt: .7, maxWidth: 560, mx: "auto" }}>Check the application and its extraction warnings. Missing information does not mean a zero balance, zero sales or no losses.</Typography>
    </Box> : <>
      {unverified > 0 && <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12.5 }}>{unverified} {unverified === 1 ? "field has" : "fields have"} missing or unverified source evidence. Compare the values with the original pages.</Alert>}
      <Box sx={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: "10px", overflow: "hidden", "& > article:first-of-type": { borderTop: 0 } }}>
        {fields.map(({ field, index }) => <FieldReview key={`${field.id || "field"}-${index}`} field={field} index={index}
          disabled={disabled || typeof onChange !== "function"} onUpdate={(updated) => updateField(index, updated)} onSourcePage={onSourcePage} />)}
      </Box>
    </>}

    <Typography sx={{ color: C.muted, fontSize: 12, mt: 1.5, lineHeight: 1.6 }}>{section.note}</Typography>
    <Typography sx={{ color: C.muted, fontSize: 11.5, mt: .5 }}>Extraction confidence estimates describe the reading of the document. They are separate from entity matching and credit assessment.</Typography>
  </Box>;
}
